import { Application, Ticker } from "pixi.js"
import Level from "./Level"
import lobbyLevel from "./level-schemas/lobbySchema"
import arena from "./level-schemas/arenaSchema"
import endGameSchema from "./level-schemas/endGameSchema"
import Clock from "./Clock"
import InfoBar from "./InfoBar"
import { Event, GameMessage, createGameMessage, readControllerMessage } from "src/common/networkingHelper"
import { WebSocketManager } from "./WebSocketManager"
import { DataArray } from "src/common/DataArray"
import { DEBUG_INPUT_COMMANDS, MATCH_CLOSING_TIME, MATCH_DURATION, WINNER_SCORE } from "./config"
import Match from "./Match"
import Lobby from "./Lobby"
import EndGame from "./EndGame"
import { Scores, SerializedGameState } from "$types/gameState"
import { PlayerIdBool, PlayerId } from "$types"
import schema3 from "./level-schemas/schema3"

export type GameInput = { playerId: PlayerId, event: Event }

export default class Game {
  private _currentLevel?: Level
  private _app: Application

  public infoBar: InfoBar

  private _matchIndex: number
  get matchIndex() {
    return this._matchIndex
  }

  private _inputQueue: GameInput[] = []
  private _ws: WebSocketManager

  private _activePlayers: PlayerIdBool = {
    1: false,
    2: false,
    3: false,
    4: false
  }
  get activePlayers() {
    return this._activePlayers
  }

  private _connectedPlayers: PlayerIdBool = {
    1: false,
    2: false,
    3: false,
    4: false
  }
  get connectedPlayers() {
    return this._connectedPlayers
  }

  private _scores: Scores = {
    1: 0,
    2: 0,
    3: 0,
    4: 0
  }
  get scores() {
    return this._scores
  }

  private _winner: PlayerId | null = null
  get winner() {
    return this._winner
  }

  public pause = false

  get currentLevel() {
    return this._currentLevel
  }

  gameState: SerializedGameState | null = null

  constructor(params: { app: Application, ws: WebSocketManager }) {
    this._app = params.app
    this._matchIndex = 0
    this._ws = params.ws

    this._ws.onmessage.push(({ data }) => {
      if (data.byteLength === 1) {
        this._handleControllerMessage(readControllerMessage(new DataArray(data)))
      } else {
        this._handleGameMessage(JSON.parse(data))
      }
    })

    this._ws.onclose.push(() => {
      for (const playerId of Object.keys(this._connectedPlayers)) {
        this._connectedPlayers[Number(playerId) as PlayerId] = false
        this.currentLevel?.setPlayerConnected(Number(playerId) as PlayerId, false)

        if (this._currentLevel instanceof Lobby) {
          this.infoBar.playerInfoDisplays[Number(playerId) as PlayerId].status = "Inactive"
        }
      }
    })

    if (DEBUG_INPUT_COMMANDS) {
      this._setDebugCommands()
    }

    this.infoBar = new InfoBar({ x: 0, y: 0 })
    this._app.stage.addChild(this.infoBar)

    this._ws.send(createGameMessage({ type: "GET_GAME_STATE" }))
  }

  init(gameState: SerializedGameState) {
    this.gameState = gameState
    this._activePlayers = gameState.activePlayers
    Object.values(gameState.users).forEach((user) => this._connectedPlayers[user.playerId] = !user.disconnectionTime)

    if (!gameState.startAt) {
      this._initLobby()
      Object.entries(this._connectedPlayers).forEach(([playerId, connected]) => {
        this.infoBar.playerInfoDisplays[Number(playerId) as PlayerId].status = connected ? "Active" : "Inactive"
      })
    } else {

      Object.entries(this._activePlayers).forEach(([id, active]) => {
        const playerId = Number(id) as PlayerId
        const player = this.infoBar.playerInfoDisplays[playerId]
        if (gameState.endAt) {
          player.status = active && gameState.winner === playerId ? "Active" : "Dead"
        } else {
          player.status = active ? "Active" : "Inactive"
        }
      })

      Object.entries(this._activePlayers).forEach(([playerId, active]) => {
        this.infoBar.playerInfoDisplays[Number(playerId) as PlayerId].status = active ? "Active" : "Inactive"
      })

      Object.entries(gameState.scores).forEach(([playerId, score]) => {
        const id = Number(playerId) as PlayerId
        this._scores[id] = score
        this.infoBar.playerInfoDisplays[id].score = score
      })

      if (gameState.endAt) {
        this._winner = gameState.winner
        this._initEndGame()
      } else {
        this._matchIndex = gameState.matches.length
        this._initMatch()
      }
    }
  }

  tickUpdate(ticker: Ticker) {
    if (!this.pause) {
      while (this._inputQueue.length > 0) {
        this._handleInput(this._inputQueue.shift()!)
      }
      Clock.tick(ticker)
      this.currentLevel?.tickUpdate(ticker)
    }
  }

  private _initLobby() {
    if (this._currentLevel) {
      this._app.stage.removeChild(this._currentLevel)
    }
    this._currentLevel = new Lobby({
      x: 0,
      y: InfoBar.InfoBarHeight,
      levelData: lobbyLevel,
      connectedPlayers: this._connectedPlayers,
      onStart: (activePlayers) => {
        this._activePlayers = activePlayers
        this._ws.send(createGameMessage({
          type: "GAME_STARTED",
          startAt: new Date().toISOString(),
          activePlayers: this._activePlayers
        }))
        this._initMatch()
      }
    })
    this._app.stage.addChild(this._currentLevel)
  }

  private _initMatch(levelData = arena) {
    this._matchIndex++
    if (this._currentLevel) {
      this._app.stage.removeChild(this._currentLevel)
    }

    const secondsLeft = MATCH_DURATION
    this.infoBar.countDownDisplay.seconds = secondsLeft
    this._currentLevel = new Match({
      x: 0,
      y: InfoBar.InfoBarHeight,
      levelData,
      matchNumber: this._matchIndex,
      closingTime: MATCH_CLOSING_TIME,
      secondsLeft,
      activePlayers: this._activePlayers,
      connectedPlayers: this._connectedPlayers,
      onGameOver: (match, winner) => {
        if (winner) {
          this._scores[winner.playerId]++
          this.infoBar.playerInfoDisplays[winner.playerId].score++
        }
        this._ws.send(createGameMessage({
          type: "END_MATCH",
          match: {
            winner: winner?.playerId || null,
            scores: this._scores,
            secondsLeft: match.secondsLeft,
            startAt: match.startAt,
            endAt: match.endAt,
          }
        }))
      },
      onExit: () => {
        const winnerPlayerId = this._getGameWinner()
        if (winnerPlayerId) {
          Object.entries(this.infoBar.playerInfoDisplays).forEach(([playerId, playerInfoDisplay]) => {
            if (Number(playerId) === winnerPlayerId) return
            playerInfoDisplay.status = "Dead"
          })
          this._winner = winnerPlayerId
          this._ws.send(createGameMessage({
            type: "END_GAME",
            endAt: new Date().toISOString(),
            winner: winnerPlayerId
          }))

          this._initEndGame()
        } else {
          this._initMatch()
        }
      },
      onClockTick: (match, secondsLeft) => {
        this.infoBar.countDownDisplay.seconds = secondsLeft
      }
    })

    this._app.stage.addChild(this._currentLevel)
  }

  private _initEndGame() {
    if (this._currentLevel) {
      this._app.stage.removeChild(this._currentLevel)
    }

    if (!this._winner) throw new Error("Winner not found")

    this._currentLevel = new EndGame({
      x: 0,
      y: InfoBar.InfoBarHeight,
      levelData: endGameSchema,
      activePlayers: this._activePlayers,
      connectedPlayers: this._connectedPlayers,
      winner: this._winner,
      onExit: () => {
        window.location.reload()
      }
    })

    this._app.stage.addChild(this._currentLevel)
  }

  private _handleGameMessage = (message: GameMessage) => {
    switch (message.type) {
      case "GAME_STATE": {
        this.init(message.gameState)
        break
      }
    }
  }

  private _handleControllerMessage = (input: GameInput) => {
    const level = this.currentLevel
    if (!level) return
    this._inputQueue.push(input)
  }

  private _handleInput = ({ playerId, event }: GameInput) => {
    const level = this.currentLevel
    if (!level) return
    const player = level.players[playerId]
    if (!player) return

    let connected = true
    switch (event) {
      case Event.MOVE_UP: {
        player.startMoving("Up")
        break
      }
      case Event.MOVE_DOWN: {
        player.startMoving("Down")
        break
      }
      case Event.MOVE_LEFT: {
        player.startMoving("Left")
        break
      }
      case Event.MOVE_RIGHT: {
        player.startMoving("Right")
        break
      }
      case Event.MOVE_STOP: {
        player.stopMoving()
        break
      }
      case Event.ACTION_A_DOWN: {
        player.placeBomb()
        break
      }
      case Event.ACTION_B_DOWN: {
        console.log("Action B down")
        break
      }
      case Event.DISCONNECTED: {
        connected = false
        break
      }
    }

    this._connectedPlayers[playerId] = connected
    level.setPlayerConnected(playerId, connected)

    if (level instanceof Lobby) {
      this.infoBar.playerInfoDisplays[playerId].status = connected ? "Active" : "Inactive"
    }
  }

  private _setDebugCommands() {
    let selectedPlayer: PlayerId = 1
    let lastKeyDown = ""
    document.addEventListener("keydown", (event) => {
      if (event.key === lastKeyDown) return
      lastKeyDown = event.key
      switch (event.key) {
        case "1":
          selectedPlayer = 1
          break
        case "2":
          selectedPlayer = 2
          break
        case "3":
          selectedPlayer = 3
          break
        case "4":
          selectedPlayer = 4
          break
        case "ArrowLeft":
          this._handleControllerMessage({ playerId: 1, event: Event.MOVE_LEFT })
          break
        case "a":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.MOVE_LEFT })
          break
        case "ArrowUp":
          this._handleControllerMessage({ playerId: 1, event: Event.MOVE_UP })
          break
        case "w":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.MOVE_UP })
          break
        case "ArrowDown":
          this._handleControllerMessage({ playerId: 1, event: Event.MOVE_DOWN })
          break
        case "s":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.MOVE_DOWN })
          break
        case "ArrowRight":
          this._handleControllerMessage({ playerId: 1, event: Event.MOVE_RIGHT })
          break
        case "d":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.MOVE_RIGHT })
          break
        case "Shift":
          this._handleControllerMessage({ playerId: 1, event: Event.ACTION_A_DOWN })
          break
        case "q":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.ACTION_A_DOWN })
          break
        case "p":
          this.pause = !this.pause
          break
        case "0":
          // start debug level
          this._activePlayers = {
            1: true,
            2: true,
            3: true,
            4: true
          }
          this._initMatch(schema3)
      }
    })

    document.addEventListener("keyup", (event) => {
      if (event.key !== lastKeyDown) return
      lastKeyDown = ""
      switch (event.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
          this._handleControllerMessage({ playerId: 1, event: Event.MOVE_STOP })
          break
        case "a":
        case "w":
        case "s":
        case "d":
          this._handleControllerMessage({ playerId: selectedPlayer, event: Event.MOVE_STOP })
          break
      }
    })
  }

  private _getGameWinner = (): PlayerId | null => {
    const scores = Object.values(this._scores)
    const maxScore = Math.max(...scores)
    if (maxScore >= WINNER_SCORE) {
      const winner = Object.entries(this._scores).filter(([, score]) => score === maxScore)
      if (winner.length > 1) {
        return null
      } else {
        return Number(winner[0][0]) as PlayerId

      }
    } else {
      return null
    }
  }
}
